import type { Config } from "tailwindcss";
import workflowsPreset from "./app/common/workflows/tailwind-preset";

export default {
  content: ["./app/**/*.{js,jsx,ts,tsx}"],
  presets: [workflowsPreset],
  theme: {
    extend: {
      // ── Duplicate-class warning ──────────────────────────────────────────
      // animate-client-ctx-fade-in and animate-client-ctx-pulse are defined
      // in app/common/workflows/tailwind-preset.ts (inherited via `presets`).
      // Do NOT redefine them here — edit the preset.
      // ─────────────────────────────────────────────────────────────────────
      // Override Tailwind's default `font-sans` so utility classes (and the
      // form-control inheritance chain set by preflight) prefer Roboto first
      // and only fall back to ui-sans-serif / system-ui. Without this,
      // `font-sans` on any component overrode the body-level Roboto rule by
      // class specificity (0,0,1,0 vs 0,0,0,1) and text rendered as Segoe UI
      // on Windows / SF Pro on macOS rather than Roboto. Calibri Bold kept
      // as the legacy non-webfont fallback.
      fontFamily: {
        sans: [
          "Roboto",
          '"Calibri Bold"',
          "ui-sans-serif",
          "system-ui",
          "sans-serif",
          '"Apple Color Emoji"',
          '"Segoe UI Emoji"',
          '"Segoe UI Symbol"',
          '"Noto Color Emoji"',
        ],
        // Highstreet headline face (brand guide); used by the leader Home (Feature 28498).
        heading: [
          "Poppins",
          "Roboto",
          '"Calibri Bold"',
          "ui-sans-serif",
          "system-ui",
          "sans-serif",
        ],
      },
      fontSize: {
        "3xs": ["0.5rem", "0.75rem"],
        "2xs": ["0.625rem", "0.875rem"],
        xs: ["0.75rem", "1.125rem"],
        sm: ["0.875rem", "1.25rem"],
        md: ["1rem", "1.5rem"],
        lg: ["1.125rem", "1.75rem"],
        xl: ["1.25rem", "1.875rem"],
        "display-2xs": ["1rem", "1.5rem"],
        "display-xs": ["1.5rem", "2rem"],
        "display-sm": ["1.875rem", "2.375rem"],
        "display-md": ["2.25rem", "2.75rem"],
        "display-lg": ["3rem", "3.75rem"],
        "display-xl": ["3.75rem", "4.5rem"],
        "display-2xl": ["4.5rem", "5.625rem"],
        "kpi-title-home": ["1.25rem", "1.25rem"],
        "kpi-title-general": ["0.875rem", "1.25rem"],
        "kpi-value-home": ["3.75rem", "4.5rem"],
        "kpi-value-general": ["2.25rem", "2.75rem"],
      },
      fontWeight: {
        regular: "400",
      },
      colors: {
        lotus: "#FFDD00",
        sunrise: "#FFC600",
        blossom: "#FFA400",
        lemon: "#FFF3A5",
        sunshine: "#FFEB68",
        pumpkin: "#F37B24",
        amber: "#E4823C",
        // v3b brand palette — ember ramp (PRD example values: 500=#F37B24, 50=#FFE5D4)
        ember: {
          50: "#FFE5D4",
          100: "#FFD4B8",
          500: "#F37B24",
          700: "#C25712",
        },
        // illuminate → alias lotus (warm yellow highlight); glow → alias sunrise
        // daffodil → alias lemon; ignite → alias blossom
        // These are safe fallbacks until the full brand guide is available.
        illuminate: "#FFDD00",
        glow: "#FFC600",
        daffodil: "#FFF3A5",
        ignite: "#FFA400",
        dividergray: "#A1A1A1",
        base: {
          white: "#FFFFFF",
          black: "#000000",
          transparent: "#FFFFFF 0%",
        },
        primary: {
          brand: {
            25: "#FCFAFF",
            50: "#F9F5FF",
            100: "#F4EBFF",
            200: "#E9D7FE",
            300: "#D6BBFB",
            400: "#BC92F6",
            500: "#9E77ED",
            600: "#7F56D9",
            700: "#6941C6",
            800: "#53389E",
            900: "#42307D",
            950: "#2C1C5F",
          },
          error: {
            25: "#FFFBFA",
            50: "#FEF3F2",
            100: "#FEE4E2",
            200: "#FECDCA",
            300: "#FDA29B",
            400: "#F97066",
            500: "#F04438",
            600: "#D92D20",
            700: "#B42318",
            800: "#912018",
            900: "#7A271A",
            950: "#55160C",
          },
          warning: {
            25: "#FFFCF5",
            50: "#FFFAEB",
            100: "#FEF0C7",
            200: "#FEDF89",
            300: "#FEC84B",
            400: "#FDB022",
            500: "#F79009",
            600: "#DC6803",
            700: "#B54708",
            800: "#93370D",
            900: "#7A2E0E",
            950: "#4E1D09",
          },
          success: {
            25: "#F6FEF9",
            50: "#ECFDF3",
            100: "#DCFAE6",
            200: "#ABEFC6",
            300: "#75E0A7",
            400: "#47CD89",
            500: "#17B26A",
            600: "#079455",
            700: "#067647",
            800: "#085D3A",
            900: "#074D31",
            950: "#053321",
          },
          gray: {
            light: {
              25: "#FCFCFD",
              50: "#F9FAFB",
              100: "#F2F4F7",
              200: "#EAECF0",
              300: "#D0D5DD",
              400: "#98A2B3",
              500: "#667085",
              600: "#475467",
              700: "#344054",
              800: "#182230",
              900: "#101828",
              950: "#0C111D",
            },
            dark: {
              25: "#FAFAFA",
              50: "#F5F5F6",
              100: "#F0F1F1",
              200: "#ECECED",
              300: "#CECFD2",
              400: "#94969C",
              500: "#85888E",
              600: "#61646C",
              700: "#333741",
              800: "#1F242F",
              900: "#161B26",
              950: "#0C111D",
            },
          },
        },
        secondary: {
          gray: {
            blue: {
              50: "#f8f9fc",
              100: "#eaecf5",
              200: "#d5d9eb",
              300: "#b3b8db",
              400: "#717bbc",
              500: "#4e5ba6",
              600: "#3e4784",
              700: "#363f72",
              800: "#293056",
              900: "#101323",
              950: "#0d0f1c",
            },
            cool: {
              25: "#fcfcfd",
              50: "#f9f9fb",
              100: "#eff1f5",
              200: "#dcdfea",
              300: "#b9c0d4",
              400: "#7d89b0",
              500: "#5d6b98",
              600: "#4a5578",
              700: "#404968",
              800: "#30374f",
              900: "#111322",
              950: "#0e101b",
            },
            modern: {
              25: "#fcfcfd",
              50: "#f8fafc",
              100: "#eef2f6",
              200: "#e3e8ef",
              300: "#cdd5df",
              400: "#9aa4b2",
              500: "#697586",
              600: "#4b5565",
              700: "#364152",
              800: "#202939",
              900: "#121926",
              950: "#0d121c",
            },
            neutral: {
              25: "#fcfcfd",
              50: "#f9fafb",
              100: "#f3f4f6",
              200: "#e5e7eb",
              300: "#d2d6db",
              400: "#9da4ae",
              500: "#6c737f",
              600: "#4d5761",
              700: "#384250",
              800: "#1f2a37",
              900: "#111927",
              950: "#0d121c",
            },
            iron: {
              25: "#fcfcfc",
              50: "#fafafa",
              100: "#f4f4f5",
              200: "#e4e4e7",
              300: "#d1d1d6",
              400: "#a0a0ab",
              500: "#70707b",
              600: "#51525c",
              700: "#3f3f46",
              800: "#26272b",
              900: "#1a1a1e",
              950: "#131316",
            },
            true: {
              25: "#fcfcfc",
              50: "#fafafa",
              100: "#f5f5f5",
              200: "#e5e5e5",
              300: "#d6d6d6",
              400: "#a3a3a3",
              500: "#737373",
              600: "#525252",
              700: "#424242",
              800: "#292929",
              900: "#141414",
              950: "#0f0f0f",
            },
            warm: {
              25: "#fdfdfc",
              50: "#fafaf9",
              100: "#f5f5f4",
              200: "#e7e5e4",
              300: "#d7d3d0",
              400: "#a9a29d",
              500: "#79716b",
              600: "#57534e",
              700: "#44403c",
              800: "#292524",
              900: "#1c1917",
              950: "#171412",
            },
          },
          moss: {
            25: "#fafdf7",
            50: "#f5fbee",
            100: "#e6f4d7",
            200: "#ceeab0",
            300: "#acdc79",
            400: "#86cb3c",
            500: "#669f2a",
            600: "#4f7a21",
            700: "#3f621a",
            800: "#335015",
            900: "#2b4212",
            950: "#1a280b",
          },
          green: {
            25: "#f6fef9",
            50: "#edfcf2",
            100: "#d3f8df",
            200: "#aaf0c4",
            300: "#73e2a3",
            400: "#3ccb7f",
            500: "#16b364",
            600: "#099250",
            700: "#087443",
            800: "#095c37",
            900: "#084c2e",
            950: "#052e1c",
            light: {
              25: "#fafef5",
              50: "#f3fee7",
              100: "#e3fbcc",
              200: "#d0f8ab",
              300: "#a6ef67",
              400: "#85e13a",
              500: "#66c61c",
              600: "#4ca30d",
              700: "#3b7c0f",
              800: "#326212",
              900: "#2b5314",
              950: "#15290a",
            },
          },
          teal: {
            25: "#f6fefc",
            50: "#f0fdf9",
            100: "#ccfbef",
            200: "#99f6e0",
            300: "#5fe9d0",
            400: "#2ed3b7",
            500: "#15b79e",
            600: "#0e9384",
            700: "#107569",
            800: "#125d56",
            900: "#134e48",
            950: "#0a2926",
          },
          cyan: {
            25: "#f5feff",
            50: "#ecfdff",
            100: "#cff9fe",
            200: "#a5f0fc",
            300: "#67e3f9",
            400: "#22ccee",
            500: "#06aed4",
            600: "#088ab2",
            700: "#0e7090",
            800: "#155b75",
            900: "#164c63",
            950: "#0d2d3a",
          },
          blue: {
            25: "#f5faff",
            50: "#eff8ff",
            100: "#d1e9ff",
            200: "#b2ddff",
            300: "#84caff",
            400: "#53b1fd",
            500: "#2e90fa",
            600: "#1570ef",
            700: "#175cd3",
            800: "#1849a9",
            900: "#194185",
            950: "#102a56",
            light: {
              25: "#f5fbff",
              50: "#f0f9ff",
              100: "#e0f2fe",
              200: "#b9e6fe",
              300: "#7cd4fd",
              400: "#36bffa",
              500: "#0ba5ec",
              600: "#0086c9",
              700: "#026aa2",
              800: "#065986",
              900: "#0b4a6f",
              950: "#062c41",
            },
            dark: {
              25: "#f5f8ff",
              50: "#eff4ff",
              100: "#d1e0ff",
              200: "#b2ccff",
              300: "#84adff",
              400: "#528bff",
              500: "#2970ff",
              600: "#155eef",
              700: "#004eeb",
              800: "#0040c1",
              900: "#00359e",
              950: "#002266",
            },
          },
          indigo: {
            25: "#f5f8ff",
            50: "#eef4ff",
            100: "#e0eaff",
            200: "#c7d7fe",
            300: "#a4bcfd",
            400: "#8098f9",
            500: "#6172f3",
            600: "#444ce7",
            700: "#3538cd",
            800: "#2d31a6",
            900: "#2d3282",
            950: "#1f235b",
          },
          violet: {
            25: "#fbfaff",
            50: "#f5f3ff",
            100: "#ece9fe",
            200: "#ddd6fe",
            300: "#c3b5fd",
            400: "#a48afb",
            500: "#875bf7",
            600: "#7839ee",
            700: "#6927da",
            800: "#5720b7",
            900: "#491c96",
            950: "#2e125e",
          },
          purple: {
            25: "#fafaff",
            50: "#f4f3ff",
            100: "#ebe9fe",
            200: "#d9d6fe",
            300: "#bdb4fe",
            400: "#9b8afb",
            500: "#7a5af8",
            600: "#6938ef",
            700: "#5925dc",
            800: "#4a1fb8",
            900: "#3e1c96",
            950: "#27115f",
          },
          fuchsia: {
            25: "#fefaff",
            50: "#fdf4ff",
            100: "#fbe8ff",
            200: "#f6d0fe",
            300: "#eeaafd",
            400: "#e478fa",
            500: "#d444f1",
            600: "#ba24d5",
            700: "#9f1ab1",
            800: "#821890",
            900: "#6f1877",
            950: "#47104c",
          },
          pink: {
            25: "#fef6fb",
            50: "#fdf2fa",
            100: "#fce7f6",
            200: "#fcceee",
            300: "#faa7e0",
            400: "#f670c7",
            500: "#ee46bc",
            600: "#dd2590",
            700: "#c11574",
            800: "#9e165f",
            900: "#851651",
            950: "#4e0d30",
          },
          rose: {
            25: "#fff5f6",
            50: "#fff1f3",
            100: "#ffe4e8",
            200: "#fecdd6",
            300: "#fea3b4",
            400: "#fd6f8e",
            500: "#f63d68",
            600: "#e31b54",
            700: "#c01048",
            800: "#a11043",
            900: "#89123e",
            950: "#510b24",
          },
          orange: {
            25: "#fefaf5",
            50: "#fef6ee",
            100: "#fdead7",
            200: "#f9dbaf",
            300: "#f7b27a",
            400: "#f38744",
            500: "#ef6820",
            600: "#e04f16",
            700: "#b93815",
            800: "#932f19",
            900: "#772917",
            950: "#511c10",
            dark: {
              25: "#fff9f5",
              50: "#fff4ed",
              100: "#ffe6d5",
              200: "#ffd6ae",
              300: "#ff9c66",
              400: "#ff692e",
              500: "#ff4405",
              600: "#e62e05",
              700: "#bc1b06",
              800: "#97180c",
              900: "#771a0d",
              950: "#57130a",
            },
          },
          yellow: {
            20: "#fff7bf",
            25: "#fefdf0",
            50: "#fefbe8",
            100: "#fef7c3",
            200: "#feee95",
            300: "#fde272",
            400: "#fac515",
            500: "#eaaa08",
            600: "#ca8504",
            700: "#a15c07",
            800: "#854a0e",
            900: "#713b12",
            950: "#542c0d",
            975: "#ffc600",
          },
        },
      },
      boxShadow: {
        "top-bottom":
          "0 -3px 4px rgba(0, 0, 0, 0.1), 0 3px 4px rgba(0, 0, 0, 0.1)",
      },
      keyframes: {
        "orachat-welcome-hide": {
          "0%": {
            opacity: "1",
            height: "auto",
          },
          "100%": {
            opacity: "0",
            height: "0",
          },
        },
        "orachat-conversation-show": {
          "0%": {
            opacity: "0",
            height: "0",
          },
          "50%": {
            opacity: "0",
            height: "auto",
          },
          "100%": {
            opacity: "1",
            height: "auto",
          },
        },
        "filter-expand": {
          "0%": {
            maxHeight: "0",
            opacity: "0",
            transform: "scaleY(0)",
            transformOrigin: "top",
          },
          "100%": {
            maxHeight: "500px",
            opacity: "1",
            transform: "scaleY(1)",
            transformOrigin: "top",
          },
        },
        "filter-collapse": {
          "0%": {
            maxHeight: "500px",
            opacity: "1",
            transform: "scaleY(1)",
            transformOrigin: "top",
          },
          "100%": {
            maxHeight: "0",
            opacity: "0",
            transform: "scaleY(0)",
            transformOrigin: "top",
          },
        },
        "kanban-slide-in": {
          "0%": { opacity: "0", transform: "translateY(16px)" },
          "100%": { opacity: "1", transform: "translateY(0)" },
        },
        "policy-check-halo": {
          "0%, 100%": {
            boxShadow:
              "0 0 0 2px #FFC600, 0 0 0 6px rgba(255, 198, 0, 0.35)",
          },
          "50%": {
            boxShadow:
              "0 0 0 4px #FFC600, 0 0 0 14px rgba(255, 198, 0, 0.55)",
          },
        },
        "policy-check-shimmer": {
          "0%": { transform: "translateX(-100%)" },
          "100%": { transform: "translateX(400%)" },
        },
        "policy-check-slide-left": {
          "0%": { opacity: "0", transform: "translateX(-28px)" },
          "100%": { opacity: "1", transform: "translateX(0)" },
        },
        "policy-check-slide-right": {
          "0%": { opacity: "0", transform: "translateX(28px)" },
          "100%": { opacity: "1", transform: "translateX(0)" },
        },
        "policy-check-fade-up": {
          "0%": {
            opacity: "0",
            transform: "translateY(6px) scale(0.97)",
          },
          "100%": {
            opacity: "1",
            transform: "translateY(0) scale(1)",
          },
        },
        // Firefly mascot (OraFireflyAnimated): slow lissajous orbit, soft halo
        // pulse, and a 400ms shudder for the error tone. prefers-reduced-motion
        // is handled in the component (these classes are dropped), so the
        // keyframes only ever run in the full-motion path.
        "firefly-orbit": {
          "0%, 100%": { transform: "translate(0px, 0px)" },
          "25%": { transform: "translate(5px, -4px)" },
          "50%": { transform: "translate(0px, -7px)" },
          "75%": { transform: "translate(-5px, -4px)" },
        },
        "firefly-halo": {
          "0%, 100%": { opacity: "0.5", transform: "scale(0.9)" },
          "50%": { opacity: "0.9", transform: "scale(1.08)" },
        },
        "ora-shudder": {
          "0%, 100%": { transform: "translateX(0)" },
          "20%, 60%": { transform: "translateX(-3px)" },
          "40%, 80%": { transform: "translateX(3px)" },
        },
      },
      animation: {
        orachatwelcomehide: "orachat-welcome-hide 1s forwards",
        orachatconversationshow: "orachat-conversation-show 1s forwards",
        "filter-expand": "filter-expand 0.4s ease-out forwards",
        "filter-collapse": "filter-collapse 0.3s ease-in forwards",
        "kanban-slide-in": "kanban-slide-in 0.4s ease-out forwards",
        "policy-check-halo":
          "policy-check-halo 400ms ease-out 2",
        "policy-check-shimmer":
          "policy-check-shimmer 1.8s ease-in-out infinite",
        "policy-check-slide-left":
          "policy-check-slide-left 480ms cubic-bezier(.22,.68,0,1.2) forwards",
        "policy-check-slide-right":
          "policy-check-slide-right 480ms cubic-bezier(.22,.68,0,1.2) 200ms forwards",
        "policy-check-fade-up":
          "policy-check-fade-up 280ms ease-out 320ms forwards",
        "firefly-orbit": "firefly-orbit 6s ease-in-out infinite",
        "firefly-halo": "firefly-halo 2.4s ease-in-out infinite",
        "ora-shudder": "ora-shudder 400ms ease-in-out 1",
      },
      screens: {
        xl1440: "1440px",
      },
    },
  },
  plugins: [],
} satisfies Config;
